import React, { useState, useEffect } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'

interface Midia {
  id: string
  nome: string
  descricao: string
}

interface Indicador {
  id: string
  nome: string
  tipo: string
  categoria: string
}

interface Investimento {
  midia_id: string
  valor_previsto: number
}

interface Meta {
  indicador_tipo_id: string
  valor_meta: number
  observacao: string
}

export default function PlanejamentoPage() {
  const [clientes, setClientes] = useState<any[]>([])
  const [selectedCliente, setSelectedCliente] = useState<any>(null)
  const [planejamentos, setPlanejamentos] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [midias, setMidias] = useState<Midia[]>([])
  const [indicadoresCliente, setIndicadoresCliente] = useState<Indicador[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  
  const [formData, setFormData] = useState({
    mes_competencia: '',
    observacoes: ''
  })
  
  const [investimentos, setInvestimentos] = useState<Investimento[]>([])
  const [metas, setMetas] = useState<Meta[]>([])
  const [novaMidia, setNovaMidia] = useState('')
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedPlanejamento, setSelectedPlanejamento] = useState<any>(null)
  const [editingPlanejamentoId, setEditingPlanejamentoId] = useState<string | null>(null)

  useEffect(() => {
    fetchClientes()
    fetchMidias()
  }, [])

  const fetchClientes = async () => {
    try {
      const res = await fetch('/api/gestao/clientes')
      if (res.ok) {
        const response = await res.json()
        const clientesData = response.data || response
        setClientes(Array.isArray(clientesData) ? clientesData : [])
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error)
      setClientes([])
    }
  }

  const fetchMidias = async () => {
    try {
      const res = await fetch('/api/gestao/kpis/midias')
      if (res.ok) {
        const data = await res.json()
        setMidias(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error('Erro ao buscar mídias:', error)
    }
  }

  const fetchPlanejamentos = async (clienteId: string) => {
    const res = await fetch(`/api/gestao/kpis/planejamento?cliente_id=${clienteId}`)
    if (res.ok) {
      const data = await res.json()
      setPlanejamentos(Array.isArray(data) ? data : [])
    }
  }

  const fetchIndicadoresCliente = async (clienteId: string) => {
    try {
      // Buscar todos os indicadores disponíveis ao invés de apenas os do cliente
      const res = await fetch('/api/gestao/kpis/indicador-tipos')
      if (res.ok) {
        const data = await res.json()
        if (Array.isArray(data)) {
          setIndicadoresCliente(data)
        }
      }
    } catch (error) {
      console.error('Erro ao buscar indicadores:', error)
    }
  }

  const handleSelectCliente = (cliente: any) => {
    setSelectedCliente(cliente)
    fetchPlanejamentos(cliente.id)
  }

  const handleOpenModal = async () => {
    if (!selectedCliente) return
    setLoading(true)
    await fetchIndicadoresCliente(selectedCliente.id)
    setInvestimentos([])
    setMetas([])
    setFormData({ mes_competencia: '', observacoes: '' })
    setEditingPlanejamentoId(null)
    setShowModal(true)
    setLoading(false)
  }

  const handleEditPlanejamento = async (planejamento: any) => {
    setLoading(true)
    await fetchIndicadoresCliente(selectedCliente.id)

    let dadosCompletos = planejamento
    if (!planejamento.planejamento_investimentos || !planejamento.planejamento_metas) {
      const res = await fetch(`/api/gestao/kpis/planejamento?id=${planejamento.id}`)
      if (res.ok) dadosCompletos = await res.json()
    }

    setEditingPlanejamentoId(dadosCompletos.id)
    setFormData({
      mes_competencia: dadosCompletos.mes_competencia.substring(0, 7),
      observacoes: dadosCompletos.observacoes || ''
    })
    setInvestimentos((dadosCompletos.planejamento_investimentos || []).map((inv: any) => ({
      midia_id: inv.midia_id,
      valor_previsto: parseFloat(inv.valor_previsto)
    })))
    setMetas((dadosCompletos.planejamento_metas || []).map((meta: any) => ({
      indicador_tipo_id: meta.indicador_tipo_id,
      valor_meta: parseFloat(meta.valor_meta),
      observacao: meta.observacao || ''
    })))
    setShowViewModal(false)
    setShowModal(true)
    setLoading(false)
  }

  const handleAddInvestimento = (midiaId: string) => {
    if (investimentos.find(i => i.midia_id === midiaId)) return
    setInvestimentos([...investimentos, { midia_id: midiaId, valor_previsto: 0 }])
  }

  const handleRemoveInvestimento = (midiaId: string) => {
    setInvestimentos(investimentos.filter(i => i.midia_id !== midiaId))
  }

  const handleUpdateInvestimento = (midiaId: string, valor: number) => {
    setInvestimentos(investimentos.map(i => 
      i.midia_id === midiaId ? { ...i, valor_previsto: valor } : i
    ))
  }

  const formatarValorMonetario = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor)
  }

  const formatarValorInteiro = (valor: number) => {
    return Math.round(valor).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
  }

  const handleValorInvestimentoMonetarioChange = (midiaId: string, novoValor: string) => {
    // Remove tudo exceto dígitos
    const apenasNumeros = novoValor.replace(/\D/g, '')
    
    // Converte para número (em centavos)
    const valorEmCentavos = parseInt(apenasNumeros) || 0
    
    // Converte centavos para reais e salva
    const valorEmReais = valorEmCentavos / 100
    handleUpdateInvestimento(midiaId, valorEmReais)
  }

  const getValorInvestimentoFormatado = (midiaId: string) => {
    const investimento = investimentos.find(i => i.midia_id === midiaId)
    if (!investimento || !investimento.valor_previsto) return ''
    return formatarValorMonetario(investimento.valor_previsto)
  }

  const handleAddMeta = (indicadorId: string) => {
    if (metas.find(m => m.indicador_tipo_id === indicadorId)) return
    setMetas([...metas, { indicador_tipo_id: indicadorId, valor_meta: 0, observacao: '' }])
  }

  const handleRemoveMeta = (indicadorId: string) => {
    setMetas(metas.filter(m => m.indicador_tipo_id !== indicadorId))
  }

  const handleUpdateMeta = (indicadorId: string, campo: string, valor: any) => {
    setMetas(metas.map(m => 
      m.indicador_tipo_id === indicadorId ? { ...m, [campo]: valor } : m
    ))
  }

  const handleValorMetaInteiroChange = (indicadorId: string, novoValor: string) => {
    // Remove tudo exceto dígitos
    const apenasNumeros = novoValor.replace(/\D/g, '')
    
    // Converte para número inteiro
    const valorInteiro = parseInt(apenasNumeros) || 0
    handleUpdateMeta(indicadorId, 'valor_meta', valorInteiro)
  }

  const handleValorMetaMonetarioChange = (indicadorId: string, novoValor: string) => {
    // Remove tudo exceto dígitos
    const apenasNumeros = novoValor.replace(/\D/g, '')
    
    // Converte para número (em centavos)
    const valorEmCentavos = parseInt(apenasNumeros) || 0
    
    // Converte centavos para reais e salva
    const valorEmReais = valorEmCentavos / 100
    handleUpdateMeta(indicadorId, 'valor_meta', valorEmReais)
  }

  const getValorMetaFormatado = (indicadorId: string) => {
    const meta = metas.find(m => m.indicador_tipo_id === indicadorId)
    if (!meta || !meta.valor_meta) return ''
    
    if (isIntegerIndicator(indicadorId)) {
      return formatarValorInteiro(meta.valor_meta)
    }
    return formatarValorMonetario(meta.valor_meta)
  }

  const handleCriarNovaMidia = async () => {
    if (!novaMidia.trim()) return
    try {
      const res = await fetch('/api/gestao/kpis/midias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: novaMidia, ativo: true })
      })
      if (res.ok) {
        await fetchMidias()
        setNovaMidia('')
        alert('Mídia criada com sucesso!')
      }
    } catch (error) {
      console.error('Erro ao criar mídia:', error)
    }
  }

  const handleViewPlanejamento = async (planejamento: any) => {
    setLoading(true)
    try {
      // Carregar indicadores para exibir nomes corretos
      await fetchIndicadoresCliente(selectedCliente.id)
      
      const res = await fetch(`/api/gestao/kpis/planejamento?id=${planejamento.id}`)
      if (res.ok) {
        const data = await res.json()
        setSelectedPlanejamento(data)
        setShowViewModal(true)
      }
    } catch (error) {
      console.error('Erro ao buscar planejamento:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeletePlanejamento = async (planejamentoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este planejamento?\n\nATENÇÃO: Só é possível excluir planejamentos sem lançamentos vinculados.')) return
    
    setLoading(true)
    try {
      const res = await fetch(`/api/gestao/kpis/planejamento?id=${planejamentoId}`, {
        method: 'DELETE'
      })
      if (res.ok) {
        alert('Planejamento excluído com sucesso!')
        fetchPlanejamentos(selectedCliente.id)
      } else {
        const data = await res.json()
        alert(`Não foi possível excluir:\n\n${data.error || 'Erro desconhecido'}`)
      }
    } catch (error) {
      console.error('Erro ao excluir planejamento:', error)
      alert('Erro ao excluir planejamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const isEditing = !!editingPlanejamentoId
      const url = isEditing
        ? `/api/gestao/kpis/planejamento/completo?id=${editingPlanejamentoId}`
        : '/api/gestao/kpis/planejamento/completo'

      const body = isEditing
        ? {
            observacoes: formData.observacoes,
            investimentos: investimentos.filter(i => i.valor_previsto > 0),
            metas: metas.filter(m => m.valor_meta > 0)
          }
        : {
            cliente_id: selectedCliente.id,
            mes_competencia: formData.mes_competencia + '-01',
            observacoes: formData.observacoes,
            investimentos: investimentos.filter(i => i.valor_previsto > 0),
            metas: metas.filter(m => m.valor_meta > 0)
          }

      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })

      const data = await res.json()

      if (res.ok) {
        alert(isEditing
          ? 'Planejamento atualizado com sucesso!\n\n⚠️ ATENÇÃO: Refaça os cálculos semana a semana em KPIs → Indicadores Calculados para que o dashboard reflita as novas informações.'
          : 'Planejamento criado com sucesso!\n\n⚠️ ATENÇÃO: Após lançar os resultados, refaça os cálculos em KPIs → Indicadores Calculados para que o dashboard exiba os dados corretamente.'
        )
        fetchPlanejamentos(selectedCliente.id)
        setShowModal(false)
        setEditingPlanejamentoId(null)
        setFormData({ mes_competencia: '', observacoes: '' })
        setInvestimentos([])
        setMetas([])
      } else if (res.status === 409) {
        alert(
          '⚠️ Já existe um planejamento para este cliente neste mês.\n\n' +
          'Para alterar metas ou investimentos, feche este formulário e clique no botão ✏️ (Editar) ao lado do planejamento existente na lista.'
        )
      } else {
        alert(`Erro: ${data.error || 'Erro desconhecido'}`)
      }
    } catch (error) {
      console.error('Erro ao salvar planejamento:', error)
      alert('Erro ao salvar planejamento')
    } finally {
      setLoading(false)
    }
  }

  const getMidiaNome = (midiaId: string) => {
    return midias.find(m => m.id === midiaId)?.nome || 'Mídia desconhecida'
  }

  const getIndicadorNome = (indicadorId: string) => {
    return indicadoresCliente.find(i => i.id === indicadorId)?.nome || 'Indicador desconhecido'
  }

  const isIntegerIndicator = (indicadorId: string) => {
    const indicador = indicadoresCliente.find(i => i.id === indicadorId)
    if (!indicador) return false
    
    const indicadoresInteiros = [
      'Leads Gerados',
      'Leads Agendados',
      'Comparecimento',
      'Agendamento',
      'Total de Vendas',
      'Quantidade de Vendas'
    ]
    
    return indicadoresInteiros.some(ind => 
      indicador.nome.toLowerCase().includes(ind.toLowerCase())
    )
  }

  const filteredClientes = clientes.filter((cliente: any) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      cliente.nome_empresa?.toLowerCase().includes(searchLower) ||
      cliente.nome_completo?.toLowerCase().includes(searchLower) ||
      cliente.email?.toLowerCase().includes(searchLower)
    )
  })

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Planejamento Mensal</h1>
            <p className="text-gray-600">Defina metas e investimentos por cliente e competência</p>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Lista de Clientes */}
            <div className="col-span-4">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Clientes
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">{clientes.length} clientes</p>
                </div>
                <div className="p-4">
                  {/* Campo de Busca */}
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Buscar cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[520px] overflow-y-auto">
                    {Array.isArray(filteredClientes) && filteredClientes.map((cliente: any) => (
                      <button
                        key={cliente.id}
                        onClick={() => handleSelectCliente(cliente)}
                        className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                          selectedCliente?.id === cliente.id 
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        {cliente.nome_empresa || cliente.nome_completo}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Planejamentos */}
            <div className="col-span-8">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {selectedCliente ? `Planejamentos - ${selectedCliente.nome_empresa || selectedCliente.nome_completo}` : 'Selecione um cliente'}
                      </h2>
                    </div>
                    {selectedCliente && (
                      <button
                        onClick={handleOpenModal}
                        disabled={loading}
                        className="bg-white text-green-600 px-6 py-2 rounded-xl font-semibold hover:bg-green-50 transition-all shadow-md flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Novo Planejamento
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {selectedCliente ? (
                    <div className="space-y-4">
                      {Array.isArray(planejamentos) && planejamentos.map((plan: any) => (
                        <div key={plan.id} className="p-5 border-2 border-gray-200 rounded-xl hover:border-green-400 transition-all">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-bold text-lg text-gray-800">
                                {new Date(plan.mes_competencia + 'T00:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                              </div>
                              {plan.observacoes && (
                                <div className="text-sm text-gray-600 mt-1">{plan.observacoes}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              {plan.precisa_recalculo && (
                                <span className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold border border-amber-300" title="Planejamento alterado. Refaça os cálculos em Indicadores Calculados.">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                  </svg>
                                  Recalcular
                                </span>
                              )}
                              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                                plan.status === 'concluido' ? 'bg-green-100 text-green-800' :
                                plan.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {plan.status}
                              </span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleViewPlanejamento(plan)}
                                  className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                                  title="Visualizar"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleEditPlanejamento(plan)}
                                  className="p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-all"
                                  title="Editar"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeletePlanejamento(plan.id)}
                                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                  title="Excluir"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {planejamentos.length === 0 && (
                        <div className="text-center py-16 text-gray-400">
                          <svg className="w-20 h-20 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <p className="font-medium text-lg">Nenhum planejamento criado</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-400">
                      <svg className="w-20 h-20 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <p className="font-medium text-lg">Selecione um cliente</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Modal Grande */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 px-8 py-6 z-10">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-3xl font-bold text-white">
                        {editingPlanejamentoId ? 'Editar Planejamento' : 'Novo Planejamento Mensal'}
                      </h2>
                      <p className="text-green-100 mt-1">
                        {editingPlanejamentoId ? 'Altere investimentos, metas ou observações' : 'Defina investimentos e metas para o período'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setShowModal(false); setEditingPlanejamentoId(null) }}
                      className="text-white hover:bg-green-700 p-2 rounded-lg transition-all mt-1"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-8">
                  {/* Informações Básicas */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Informações Básicas
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Mês de Competência *</label>
                        <input
                          type="month"
                          value={formData.mes_competencia}
                          onChange={(e) => !editingPlanejamentoId && setFormData({...formData, mes_competencia: e.target.value})}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all ${
                            editingPlanejamentoId
                              ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'border-gray-200 focus:border-green-500'
                          }`}
                          readOnly={!!editingPlanejamentoId}
                          required
                          title={editingPlanejamentoId ? 'O mês de competência não pode ser alterado' : ''}
                        />
                        {editingPlanejamentoId && (
                          <p className="text-xs text-gray-500 mt-1">O mês não pode ser alterado em uma edição</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">Observações</label>
                        <textarea
                          value={formData.observacoes}
                          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-all"
                          rows={3}
                          placeholder="Observações gerais sobre o planejamento..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Investimentos por Mídia */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Investimentos por Mídia
                    </h3>
                    
                    <div className="bg-blue-50 rounded-xl p-6 mb-4">
                      <div className="flex gap-3 mb-4">
                        <select
                          onChange={(e) => e.target.value && handleAddInvestimento(e.target.value)}
                          className="flex-1 px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:outline-none"
                          value=""
                        >
                          <option value="">Selecione uma mídia...</option>
                          {midias.filter(m => !investimentos.find(i => i.midia_id === m.id)).map(midia => (
                            <option key={midia.id} value={midia.id}>{midia.nome}</option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Nova mídia..."
                            value={novaMidia}
                            onChange={(e) => setNovaMidia(e.target.value)}
                            className="px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={handleCriarNovaMidia}
                            className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
                          >
                            + Criar
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {investimentos.map((inv) => (
                          <div key={inv.midia_id} className="flex items-center gap-4 bg-white p-4 rounded-xl border-2 border-blue-200">
                            <div className="flex-1">
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {getMidiaNome(inv.midia_id)}
                              </label>
                              <input
                                type="text"
                                value={getValorInvestimentoFormatado(inv.midia_id)}
                                onChange={(e) => handleValorInvestimentoMonetarioChange(inv.midia_id, e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                                placeholder="R$ 0,00"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveInvestimento(inv.midia_id)}
                              className="px-4 py-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        {investimentos.length === 0 && (
                          <p className="text-center text-gray-500 py-4">Nenhum investimento adicionado</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Metas por Indicador */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Metas por Indicador (Opcional)
                    </h3>
                    
                    <div className="bg-orange-50 rounded-xl p-6">
                      <select
                        onChange={(e) => e.target.value && handleAddMeta(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:outline-none mb-4"
                        value=""
                      >
                        <option value="">Selecione um indicador...</option>
                        {indicadoresCliente.filter(i => !metas.find(m => m.indicador_tipo_id === i.id)).map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.nome} ({ind.tipo})</option>
                        ))}
                      </select>

                      <div className="space-y-3">
                        {metas.map((meta) => (
                          <div key={meta.indicador_tipo_id} className="bg-white p-4 rounded-xl border-2 border-orange-200">
                            <div className="flex items-start gap-4">
                              <div className="flex-1 grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {getIndicadorNome(meta.indicador_tipo_id)}
                                  </label>
                                  <input
                                    type="text"
                                    value={getValorMetaFormatado(meta.indicador_tipo_id)}
                                    onChange={(e) => {
                                      if (isIntegerIndicator(meta.indicador_tipo_id)) {
                                        handleValorMetaInteiroChange(meta.indicador_tipo_id, e.target.value)
                                      } else {
                                        handleValorMetaMonetarioChange(meta.indicador_tipo_id, e.target.value)
                                      }
                                    }}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                                    placeholder={isIntegerIndicator(meta.indicador_tipo_id) ? "0" : "R$ 0,00"}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Observação</label>
                                  <input
                                    type="text"
                                    value={meta.observacao || ''}
                                    onChange={(e) => handleUpdateMeta(meta.indicador_tipo_id, 'observacao', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                                    placeholder="Observação (opcional)"
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveMeta(meta.indicador_tipo_id)}
                                className="px-4 py-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-all"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        {metas.length === 0 && (
                          <p className="text-center text-gray-500 py-4">Nenhuma meta definida (opcional)</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botões */}
                  <div className="flex justify-end gap-4 pt-6 border-t-2">
                    <button 
                      type="button" 
                      onClick={() => { setShowModal(false); setEditingPlanejamentoId(null) }}
                      className="px-8 py-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit"
                      disabled={loading}
                      className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg disabled:opacity-50"
                    >
                      {loading ? 'Salvando...' : editingPlanejamentoId ? 'Salvar Alterações' : 'Criar Planejamento'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal de Visualização */}
          {showViewModal && selectedPlanejamento && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-6 z-10">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-3xl font-bold text-white">Detalhes do Planejamento</h2>
                      <p className="text-blue-100 mt-1">
                        {new Date(selectedPlanejamento.mes_competencia + 'T00:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="text-white hover:bg-blue-700 p-2 rounded-lg transition-all"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="p-8">
                  {/* Informações Básicas */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Informações Gerais</h3>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-gray-600">Cliente:</span>
                          <p className="font-semibold text-gray-800">{selectedPlanejamento.clientes?.nome_empresa || selectedPlanejamento.clientes?.nome_completo}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Status:</span>
                          <p className="font-semibold text-gray-800">{selectedPlanejamento.status}</p>
                        </div>
                      </div>
                      {selectedPlanejamento.observacoes && (
                        <div className="mt-4">
                          <span className="text-sm text-gray-600">Observações:</span>
                          <p className="text-gray-800 mt-1">{selectedPlanejamento.observacoes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Investimentos */}
                  {selectedPlanejamento.planejamento_investimentos && selectedPlanejamento.planejamento_investimentos.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Investimentos por Mídia
                      </h3>
                      <div className="space-y-3">
                        {selectedPlanejamento.planejamento_investimentos.map((inv: any, idx: number) => (
                          <div key={idx} className="bg-blue-50 rounded-xl p-4 flex justify-between items-center">
                            <span className="font-semibold text-gray-800">{getMidiaNome(inv.midia_id)}</span>
                            <span className="text-lg font-bold text-blue-600">
                              R$ {parseFloat(inv.valor_previsto).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))}
                        <div className="bg-blue-100 rounded-xl p-4 flex justify-between items-center border-2 border-blue-300">
                          <span className="font-bold text-gray-800">Total Investimento:</span>
                          <span className="text-xl font-bold text-blue-700">
                            R$ {selectedPlanejamento.planejamento_investimentos
                              .reduce((sum: number, inv: any) => sum + parseFloat(inv.valor_previsto), 0)
                              .toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Metas */}
                  {selectedPlanejamento.planejamento_metas && selectedPlanejamento.planejamento_metas.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Metas Definidas
                      </h3>
                      <div className="space-y-3">
                        {selectedPlanejamento.planejamento_metas.map((meta: any, idx: number) => (
                          <div key={idx} className="bg-orange-50 rounded-xl p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <span className="font-semibold text-gray-800">{getIndicadorNome(meta.indicador_tipo_id)}</span>
                                {meta.observacao && (
                                  <p className="text-sm text-gray-600 mt-1">{meta.observacao}</p>
                                )}
                              </div>
                              <span className="text-lg font-bold text-orange-600">
                                Meta: {isIntegerIndicator(meta.indicador_tipo_id) 
                                  ? formatarValorInteiro(parseFloat(meta.valor_meta))
                                  : parseFloat(meta.valor_meta).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Botões */}
                  <div className="flex justify-between pt-6 border-t-2">
                    <button
                      onClick={() => handleEditPlanejamento(selectedPlanejamento)}
                      className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar Planejamento
                    </button>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="px-8 py-3 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
